/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
=========================================================
*/

import { createContext, useContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";

// Material Dashboard 2 React main context
const MaterialUI = createContext();
MaterialUI.displayName = "MaterialUIContext";

// -------------------------------
// Reducer
// -------------------------------
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV":
      return { ...state, miniSidenav: action.value };
    case "TRANSPARENT_SIDENAV":
      return { ...state, transparentSidenav: action.value };
    case "WHITE_SIDENAV":
      return { ...state, whiteSidenav: action.value };
    case "SIDENAV_COLOR":
      return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR":
      return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR":
      return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR":
      return { ...state, openConfigurator: action.value };
    case "DIRECTION":
      return { ...state, direction: action.value };
    case "LAYOUT":
      return { ...state, layout: action.value };
    case "DARKMODE":
      return { ...state, darkMode: action.value };

    // UI overrides centralizados
    case "SET_UI_OVERRIDES":
      return { ...state, uiOverrides: { ...state.uiOverrides, ...action.value } };

    // >>> NOVO: tipo de cabeçalho global ("cabecalho1" | "cabecalho2")
    case "HEADER_TYPE":
      return { ...state, headerType: action.value };

    case "SET_MAPS_KEY":
      return { ...state, maps: { ...state.maps, googleMapsKey: action.value } };

    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

// -------------------------------
// Utils do contexto
// -------------------------------

// Cor do texto (preto/branco) consoante luminosidade do fundo
const getTextColorForBackground = (hexcolor) => {
  if (!hexcolor) return "#000000";
  const h = hexcolor.replace("#", "");
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "#000000" : "#FFFFFF";
};

// ---------- helpers de cor / gradiente ----------
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  const v =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const bigint = parseInt(v, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};

const tintHex = (hex, pct) => {
  const { r, g, b } = hexToRgb(hex);
  const mixUp = (c, p) => clamp(Math.round(c + (p / 100) * (255 - c)), 0, 255);
  const mixDn = (c, p) => clamp(Math.round(c * (1 + p / 100)), 0, 255);
  const rr = (pct >= 0 ? mixUp(r, pct) : mixDn(r, pct)).toString(16).padStart(2, "0");
  const gg = (pct >= 0 ? mixUp(g, pct) : mixDn(g, pct)).toString(16).padStart(2, "0");
  const bb = (pct >= 0 ? mixUp(b, pct) : mixDn(b, pct)).toString(16).padStart(2, "0");
  return `#${rr}${gg}${bb}`;
};

const angleForDirection = (dir) => {
  if (dir === "lr") return 90;
  if (dir === "tb") return 180;
  if (dir === "rl") return 270;
  if (dir === "bt") return 0;
  return 195;
};

const getDirectionalGradient = (theme, colorOrHex, direction = "lr", step = 24) => {
  const pal = theme.palette || {};
  const grads = pal.gradients || {};
  const angle = angleForDirection(direction);

  if (grads[colorOrHex]) {
    const base = grads[colorOrHex].main || "#000000";
    const light = tintHex(base, step);
    const dark = tintHex(base, -step);
    return `linear-gradient(${angle}deg, ${light} 0%, ${dark} 100%)`;
  }

  const base =
    typeof colorOrHex === "string" && colorOrHex.trim().startsWith("#") ? colorOrHex : "#000000";
  const light = tintHex(base, step);
  const dark = tintHex(base, -step);
  return `linear-gradient(${angle}deg, ${light} 0%, ${dark} 100%)`;
};

const getGradientFromColor = (theme, colorOrHex, angle = 195, reverse = false) => {
  let dir = "lr";
  if (angle === 180) dir = "tb";
  if (angle === 270) dir = "rl";
  if (angle === 0) dir = "bt";
  if (reverse) dir = dir === "lr" ? "rl" : dir === "rl" ? "lr" : dir === "tb" ? "bt" : "tb";
  const step = 26;
  return getDirectionalGradient(theme, colorOrHex, dir, step);
};

// Seletores
const getNavbarBgColor = (controller) =>
  controller?.uiOverrides?.navbarBg ?? controller?.sidenavColor ?? "info";
const getSidenavBgColor = (controller) => controller?.uiOverrides?.sidenavBg ?? "dark";
const getAppBgColor = (controller) => controller?.uiOverrides?.appBg ?? null;

// -------------------------------
// Provider
// -------------------------------
function MaterialUIControllerProvider({ children }) {
  // === carregar preferência do utilizador (localStorage) ===
  const lsKeyHeader = "CV_HEADER_TYPE";
  let headerFromLs = "cabecalho2";
  if (typeof window !== "undefined") {
    const v = localStorage.getItem(lsKeyHeader);
    if (v === "cabecalho1" || v === "cabecalho2") headerFromLs = v;
  }

  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,

    // só pode usar: "primary"|"secondary"|"info"|"success"|"warning"|"error"|"dark"
    sidenavColor: "info",

    transparentNavbar: false,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,

    // "cabecalho1" = faixa gradient; "cabecalho2" = tradicional
    headerType: headerFromLs,

    // Overrides globais
    uiOverrides: {
      sidenavType: "dark",
      sidenavBg: "#313137",
      sidenavText: "#4f4b4bff",
      navbarBg: "#313137",
      navbarText: "#ffffff",
      gradientStep: 26,
      appBg: "#BBBECB",
      appText: null,
      appBgStep: 40,
      appBgDir: "tb",
      hideConfigurator: true,
    },

    maps: {
      // coloca aqui a tua key; se ficar "", o componente mostra aviso
      googleMapsKey: "AIzaSyCZIQbulKa6cjeBE3fjuR_wKWxOc3UT-Js",
    },
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  // === persistir alterações de headerType ===
  if (typeof window !== "undefined") {
    const cur = controller.headerType;
    if (cur && (cur === "cabecalho1" || cur === "cabecalho2")) {
      const prev = localStorage.getItem(lsKeyHeader);
      if (prev !== cur) localStorage.setItem(lsKeyHeader, cur);
    }
  }

  const value = useMemo(
    () => [
      {
        ...controller,
        getTextColorForBackground,
        getDirectionalGradient,
        getGradientFromColor,
        getNavbarBgColor,
        getSidenavBgColor,
        getAppBgColor,
      },
      dispatch,
    ],
    [controller, dispatch]
  );

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Hook
function useMaterialUIController() {
  const context = useContext(MaterialUI);
  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }
  return context;
}

MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// -------------------------------
// Ações & seletores exportados
// -------------------------------
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });
const setUiOverrides = (dispatch, value) => dispatch({ type: "SET_UI_OVERRIDES", value });

// >>> NOVO: ação para mudar o tipo de cabeçalho
const setHeaderType = (dispatch, value /* "cabecalho1" | "cabecalho2" */) =>
  dispatch({ type: "HEADER_TYPE", value });

const setGoogleMapsKey = (dispatch, value /* string */) =>
  dispatch({ type: "SET_MAPS_KEY", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setUiOverrides,
  setHeaderType,
  // seletores/utilitários:
  getDirectionalGradient,
  getNavbarBgColor,
  getSidenavBgColor,
  getAppBgColor,
  getTextColorForBackground,
};
